import { or, and, setup } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

import type { TContext, TEvent } from 'machines/types/machine-config';

export const nonDomesticTransferMachineConfig = {
  id: 'nonDomesticTransferMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'fuelType',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.nondomesticTransfer
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    fuelType: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        CHANGE_ADDRESS: {
          target: 'home'
        }
      },
      meta: {
        path: routePath.fuelType
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'personaliseQuote'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
                'isMixedNepSap',
                 'isBGECustomer'
              ])`,

            guard: or(['isMixedNepSap', 'isBGECustomer'])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ],
        NON_DOMESTIC: {
          target: 'personaliseQuote'
        }
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    mixedMeters: {
      on: {
        NEXT: {
          target: 'personaliseQuote'
        }
      },
      meta: {
        path: routePath.mixedMeters
      }
    },

    personaliseQuote: {
      on: {
        NEXT: [
          {
            target: 'paymentMethod',
            guard: 'isPrePayOnly'
          },
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.personaliseQuote
      }
    },
    paymentMethod: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.prepayInfo
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'tariffDetails'
        },
        CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    tariffDetails: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.tariffDetails
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebitType',
            description:
              "and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])",
            guard: and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'psrEntry'
          }
        ],
        CHANGE_ADDRESS: { target: 'home' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebitType: {
      on: {
        NEXT: {
          target: 'directDebit'
        }
      },
      meta: {
        path: routePath.directDebitType
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'psrEntry'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    authResponse: {
      meta: {
        path: routePath.authResponse
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.newMoveIn }
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...(events as any)
};

const { VITE_XSTATE_DEBUG } = import.meta.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  setup({
    types: {} as {
      context: TContext;
      events: TEvent;
    },
    guards
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QQJYCcwGMAuBlAhgDZwCy+mAFigHZgB0FA9gLZgDEAcgKIAaAKgG0ADAF1EoAA6NYKbCkbVxIAB6IAjGoBMQuprUA2TQHYArAGYjR42f0AaEAE915gCx0AHBaEvNLl2oBONW8AXxD7VAwcAmJYMkoaeiZWTl5BNTEkECkZOQUlVQQNbV0DY3NLaztHdXd9NytPFyEDG30jMIj0LDwiUnIqWgYWdm5+AU1MyWlZeUUswuKdPUNTCysjG3snIs0AnQCTd0CA-ZNDzXdOkEiemP6EoeTRtIEzKeyZvPnQRa1lsprSqbao7QL6Ex0EwuU7mS5mAI+FzXW7RPpxAaJYYpABCABkAPIAYQA0gB9ABSBIAqgAlbgATWEHxys3yC3U-1KqwqGy2NQQpx07nczX0ZhcdX0ahM+hR3TRsXig3oACMAK4yWiwWDUiQQfDYOAAQWo1EY6uomDArGo2FS41ESlZ3wKtQMdCEhnqLhMQncQm0oMQiICdDUzS07TqhzU8qivSVmKGGq1cF1+sNJrNFqtNrAdrYxrxhIA6pSafSuEynVkXXM3UUzIEoVZ-TYhEZ3CYjMHBf5PUdoc3-cEzFdwjcFYmHiq6KnEjq9QajbBcOrVcxZFxmPgUIQHYJa9Ncg2OQhfJ4ofogtLNl2jtsQzDw5HLmoRYFx-G7ujlVjWCNNApBobAADUiBQFc5kPZlnS+M9fndfRPW9Pw-QDIMnyKHwjHDcEYW9MxfACH9FVnACwCAkC7QgwgoMNGCxnSFkEPZJCimOFCvU0H0MMDL1sJlS46ACEUhDMQNTnaONJ1RGcMUeehALAYDGFAuiGO+NgAHFqQASQAES4MkiTxfTSTgus2J+FRkNQ3j0P9AS+zUXsw2ONpjEsfQhHMMiFP-IZYDAYgcAJCRvlgNguHMnT9PxEyADFqViqyTzZWzCkvMxr1vNyLG7dwhO8ugfH0btJICGxfQC+5FLnEKwuwCKopiwz9L4MljUMwzaS4XBcHSz5T3YuyigCIwwwMVogwMIRJqEtydGhVapuIyrNDqv9k3oJqelauZooACQJEgTJIAkwK4Yb6zG7LJVy2V8vvIqhL8vDfWhBaYyMCSTG2pMlLofbwsio62FO86yUu66BAyeDRqyxAcrymbXsfAUNEsOhLB7NobxMMdAYo4LQoO8GFBOs6Lqum7JkRzLG1R570cKzGwSETs6EMSbLARNyby2uTp3qoK9vJsG2pIfSeC4QyYa4PguFpIbjxGpnzxZm82YfYqseIvDNBMS8TB7GUpuFroEzF3a6ALVSoAcDgwEgaLmNumzmdlTQecsT8ZQDYihPqMNmgW7QA19PzkRFm2duBh20Cdl23dghHrKR73jDoCwylhHwmiE9ZPUsAJCbcsuSYarEk5T12IHd14GczzWOON5sPHccvu2cwNNGwzRiJQrsmnOIm1GI6vxft2hk+dhum-Gd5Gddc8NpQnxgj8CTeJFMwSr8QcTe8MwJR7Do49-IG5zrhe049lxWKzrX6iNmNxzHdxjcH-1IXL4wk8XCbECBOa219Sb0DAAANz4PgNAKAABmiD9LUEQYwNAu5tIe3VndZGF4XBPQML2fGPoWiDz8EbForRzhAKHtPO2MC4EIOQag9BmDGIKHTs-Nu41fBEPaBCEEO81DYT+r7TYexgjihaITBhiddz7iJPgSK6oMCwVwV7deGFwyVVMDCYi-gD4Cm-joeoRxeyYQ0IQ+Rc4ACO6pGBGg0TwteHEhGekMc2CUEoYQuGwv4PCngIyXiMBoE2ZhbFYgcU4l44wM4ZTceNDxu9CFAN8YibC5h3C6B3kESuISrZTnjjfaJjjnFcBIMafSeIyQAEVqQEmVp7F+7ieyeP4ekvwmSBR-WWMRIIXpJIGEmlEoYMTnFEgJBwXA1ISAAAU+D6WmS03hhQUleK6X4weQZc6mHaOXX0IS5RX3IjXIYEgwD4AANYEGgXEo8rjELjSWNyco6wqgBLch4ZsxgDHHCMOVMZUDFGEDqeUh5qykmFF7roM2ZhzASUCJHbCEIdDNA2McE2Q8ezAroJctAsAFBEEMlRPchAl6PNXs8xYXEHJ8WclhAUvzc5HDPu4P6Mo3JgOKRA859ACVEuoCSsl+5KXwyefdeyPEGWYUEgKCEuVJ7cuRcbfwpFTmBTtoK4lhBSXYHJeKluiSaXSrQtHOVfZgFqChLKL0Mo-BcUsHiiQCCMGyAcLgVS0CUDWlgLSMAUAUCwCAi46lUqEAbzKnobwzRh772wsRG1YlnLNjEn9YBkTNW22Bq6+QCDsCeu9b6uAAag0htUtw8N+DHLvxvJ-CS38TABLPqhLFfleLilMHi+SpLVSyDDa3aFiAEX+l0cA0wvZJ6SWbQKbQNrNg9l8IAhFegeXyRzXODBEBVLrmYLuNADhB0mojdrF67N9Y7GOLnTQexU1D1FJfcBZyZ7bt3eqfd8Cj1EmOsaDgOkTLzJVrgaZxYyTGT4NUvEatJX4I-DeXO3cKowmkuYgJehcaSUaNVeEgKAbZoTlutAO60B7oPd+39-6TI9T6gNGD1bGyBGAVCOoXp-SEVMLOnY0Iwy+GaPkywEY3J4rfaRj95G2A-r-QBsk8zjQMnOhwLqEGoP0aHaaghV5WZ3gvVk4iuMPxYtMH5cusln1auBqJsjX7JOUZk-M3AtJwNK1U1CjTZ7dZvQFJKX23cJTBPHNKCqWbzObqxFZ8TNmpNUbJFwbgtIdIMjJNweWamT01semjHTesSo518loOa5swiTnNDu+AWQN2EcSAx88ABaPs9W8XPBqxxIUtq-RWBNv8RaWNiGeg-DYTt5xDghd5S+u2C5tQZhXNmc0lprS2mwC1l5RM3BeC0BGHwUl-EClDLnUc-hoQuHFOKPFk30zLizGuDcW5sA7nJct7KZQWO+R8AEPYEIAjYT25JY4EkwnBGOEUyrpSUyakXNNq765NzblBeuTAfryvpeZiNjwhxAyeFMO9geu2Xy-bHAD0cZmxsWbnCpNSGlILQTGngxjPYckQgjN4Y23ZjvGLBOYSEXM-S3oMN2XieLyfzPUnaHS6ooJgEeyjZ7RxXt7A++cIS7lRJcWXeJCEeLQYtUptQJHGth0XiHihYid7giVyJlxzk38flHAWu0Nnew8V31To3KXF4e5QlDIiNjC0dtgj47oIevhqELVCAR0HUDYHwKQSgtBGCsHPNp+eX0UJ-RTV9NGXuvZB7D1ErKLDx2ZRBCfSTsLQwbTkuUaojAbubCSVEl2D8jewkQhx1e-T1VpQmw5dGb84fIF0AmZL9TEbOeiUCC0Sau8ux+8QOI0SXNET1C9IRE5oWqsXKubc-A9y3eHBHlYW9U1xTWEvYgQJolTgjjCdHAwTvQXgtiXv8wnpJQGE50cPwfYIQM+ep2fw0oEcLqqkQqIqBqYqbuReuU72mwngw8hwwC6GkIvgk6-oFUN+iILqbqBaRaaAPqfqZawaQEbuewXYgelChC-gQelukat6r4rOfkY44Ia+peG+9AvaYA-aS2I++CdeBwjeHKAK7+be0um8fohgngxsvENgImxG76n6h6JBQ8C6t6nO-chMLaEiYS3OXMxmf0o2IOA+omRILAEgxARoJBqO3cPOmO5wt62E16MInYFw7klQwOosbB9sygQEwqhAOIUA9UxoEgEgbuPgOe3YZUvodQng0RnWTu3hqkvhJhdo5A2A1IeuSe7cVh6OuGdhIhRQEcHgf0lsAYN4+g5R8RPhRA-hRIhA+AOovqx0IwJAjAu+PBKOL+1hGOFgeR703MU0vY9QJ2NgEYlRiRRALR9yqClhnRORth2OS0OMiINgYkbkpw4ixWIQQAA */
    ...nonDomesticTransferMachineConfig
  });
